'use client';

import { c as _c } from "react/compiler-runtime";
import { Checkbox } from '@repo/ui/components';
import { Subscribe } from '@tanstack/react-table';
import { useContext, useRef } from 'react';
import type { Row, Table } from '@tanstack/react-table';
import { TableStateContext } from '../context/table-state-context';
import { RowSelectionGesturesContext } from '../hooks/use-row-selection-gestures';
import type { ColumnDef, RowData, TableFeatures } from '../tanstack-table';
import { SELECTION_COLUMN_ID } from './selection-column-id';
export { SELECTION_COLUMN_ID };
const SELECTION_CHECKBOX_SELECTOR = '[data-table-selection-checkbox="all"]';

/** Restores keyboard focus to the first available row-selection control. */
export const focusSelectionCheckbox = (root?: ParentNode) => {
  if (typeof document === 'undefined') return;
  const container = root ?? document;
  container.querySelector<HTMLElement>(SELECTION_CHECKBOX_SELECTOR)?.focus();
};
export const isSelectionColumn = (columnId: string) => columnId === SELECTION_COLUMN_ID;
export const createSelectionColumn = <TData extends RowData, TValue = unknown>(): ColumnDef<TData, TValue> => ({
  cell: ({
    row,
    table
  }) => <SelectionCell row={row} table={table} />,
  enableHiding: false,
  enablePinning: false,
  enableResizing: false,
  enableSorting: false,
  header: ({
    table
  }) => <SelectionHeader table={table} />,
  id: SELECTION_COLUMN_ID,
  maxSize: 48,
  meta: {
    align: 'center',
    label: 'Select rows',
    lockedPinning: 'start'
  },
  minSize: 48,
  size: 48
});
export const SelectionHeader = t0 => {
  const $ = _c(7);
  const {
    table
  } = t0;
  const loadedRowCount = useContext(TableStateContext)?.loadedRowCount;
  const gestures = useContext(RowSelectionGesturesContext);
  let t1;
  if ($[0] !== gestures || $[1] !== loadedRowCount || $[2] !== table) {
    t1 = () => {
      const isIndeterminate = !table.getIsAllRowsSelected() && table.getIsSomeRowsSelected();
      return <Checkbox aria-label="Select all rows" checked={table.getIsAllRowsSelected()} data-table-selection-checkbox="all" disabled={loadedRowCount === 0} indeterminate={isIndeterminate} onCheckedChange={value => {
        table.toggleAllRowsSelected(value === true);
        gestures?.clearAnchor();
      }} onClick={_temp2} />;
    };
    $[0] = gestures;
    $[1] = loadedRowCount;
    $[2] = table;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1 || $[5] !== table.store) {
    t2 = <Subscribe selector={_temp} source={table.store} data-sentry-element="Subscribe" data-sentry-component="SelectionHeader" data-sentry-source-file="selection-column.tsx">{t1}</Subscribe>;
    $[4] = t1;
    $[5] = table.store;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const SelectionCell = t0 => {
  const $ = _c(9);
  const {
    row,
    table
  } = t0;
  const gestures = useContext(RowSelectionGesturesContext);
  const checkboxRef = useRef(null);
  let t1;
  if ($[0] !== row.id) {
    t1 = state => state.rowSelection[row.id];
    $[0] = row.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== gestures || $[3] !== row) {
    t2 = selected => <label className="absolute inset-0 flex cursor-pointer items-center justify-center has-disabled:cursor-not-allowed" {...gestures?.getSelectionCellProps(row, checkboxRef)}><Checkbox aria-label={`Select row ${row.id}`} checked={Boolean(selected)} data-table-selection-checkbox={row.id} disabled={!row.getCanSelect()} onCheckedChange={value => {
        row.toggleSelected(value === true);
        gestures?.setAnchor(row.id);
      }} onClick={_temp3} ref={checkboxRef} /></label>;
    $[2] = gestures;
    $[3] = row;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== table.store) {
    t3 = <Subscribe selector={t1} source={table.store} data-sentry-element="Subscribe" data-sentry-component="SelectionCell" data-sentry-source-file="selection-column.tsx">{t2}</Subscribe>;
    $[5] = t1;
    $[6] = t2;
    $[7] = table.store;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
function _temp(state) {
  return state.rowSelection;
}
function _temp2(event) {
  return event.stopPropagation();
}
function _temp3(event) {
  return event.stopPropagation();
}